<template>
  <div class="filter-attentions">
    <!-- Accordion Header -->
    <div class="filter-header" @click="toggleAccordion">
      <h3 class="filter-title">Filtrar Atenciones</h3>
      <button class="toggle-btn" :class="{ 'is-open': isOpen }">
        <span class="toggle-icon">{{ isOpen ? "&#10094;" : "+" }}</span>
      </button>
    </div>

    <!-- Accordion Body with animation -->
    <transition name="accordion" @after-enter="onAfterEnter" @before-leave="onBeforeLeave">
      <div v-if="isOpen" ref="accordionBody" class="filter-body">
        <!-- Include / Exclude radio buttons -->
        <div class="filter-mode">
          <label class="radio-label">
            <input type="radio" name="filterMode" value="include" v-model="filterMode" />
            <span>Incluir selección</span>
          </label>
          <label class="radio-label">
            <input type="radio" name="filterMode" value="exclude" v-model="filterMode" />
            <span>Excluir selección</span>
          </label>
        </div>

        <!-- Selectors Row -->
        <div class="filter-selectors">
          <selector-group
            label="Categoría de atención"
            pill-class="pill--category"
            :options="availableCategories"
            :selected-items="selectedCategories"
            @toggle="toggleCategory"
            @remove="toggleCategory"
          />

          <selector-group
            label="Grupo de atención"
            pill-class="pill--group"
            :options="availableGroups"
            :selected-items="selectedGroups"
            :disabled="selectedCategories.length === 0"
            @toggle="toggleGroup"
            @remove="toggleGroup"
          />

          <selector-group
            label="Tipo de atención"
            pill-class="pill--type"
            :options="availableTypes"
            :selected-items="selectedTypes"
            :disabled="selectedGroups.length === 0"
            @toggle="toggleType"
            @remove="toggleType"
          />
        </div>

        <!-- Action Buttons -->
        <div class="filter-actions">
          <button class="button is-primary" @click="applyFilters">
            Aplicar filtros
          </button>
          <button class="button is-primary is-outlined" @click="clearFilters">
            Limpiar filtros
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import SelectorGroup from "../../../shared/selector_group/SelectorGroup.vue"
import ATTENTION_CATEGORIES from "./graphql/AttentionCategories.gql"
import ATTENTION_PLANS from "./graphql/AttentionPlans.gql"
import ATTENTION_ACTIONS from "./graphql/AttentionActions.gql"

/**
 * FilterAttentions Component
 *
 * Provides an accordion-style filter panel with three cascading multi-select
 * dropdowns: Category -> Group -> Type.
 *
 * Selected items are displayed as removable pills below each respective selector.
 *
 * @emits filters-applied - Emitted when the user clicks "Aplicar filtros".
 *   Payload: { filterMode: string, categoryIds: string[], groupIds: string[], typeIds: string[] }
 * @emits filters-cleared - Emitted when the user clicks "Limpiar filtros".
 */
export default {
  name: "FilterAttentions",

  components: {
    SelectorGroup
  },

  data: function() {
    return {
      /** Whether the accordion panel is open */
      isOpen: false,

      /** Filter mode: 'include' or 'exclude' */
      filterMode: "include",

      /**
       * Selected categories - array of { id, name } objects.
       * These are the variables you will send to the backend.
       */
      selectedCategories: [],

      /**
       * Selected groups - array of { id, name, categoryId } objects.
       * These are the variables you will send to the backend.
       */
      selectedGroups: [],

      /**
       * Selected types - array of { id, name, groupId } objects.
       * These are the variables you will send to the backend.
       */
      selectedTypes: [],

      /**
       * All categories fetched from the backend.
       */
      categories: [],

      /**
       * All plans fetched from the backend (lazy loaded when categories are selected).
       */
      allPlans: [],

      /**
       * All actions fetched from the backend (lazy loaded when plans are selected).
       */
      allActions: []
    }
  },

  apollo: {
    categories: {
      query: ATTENTION_CATEGORIES,
      update(data) {
        return data.attentionCategories || []
      }
    },
    allPlans: {
      query: ATTENTION_PLANS,
      variables() {
        // Load all plans when categories are selected (pass null to get all)
        return { categoryId: null }
      },
      skip() {
        // Skip query if no categories selected (lazy loading)
        return this.selectedCategories.length === 0
      },
      update(data) {
        return data.attentionPlans || []
      }
    },
    allActions: {
      query: ATTENTION_ACTIONS,
      variables() {
        // Load all actions when plans are selected (pass null to get all)
        return { planId: null }
      },
      skip() {
        // Skip query if no plans selected (lazy loading)
        return this.selectedGroups.length === 0
      },
      update(data) {
        return data.attentionActions || []
      }
    }
  },

  computed: {
    /**
     * Available categories for the first dropdown.
     * Returns the full list of categories from props.
     * @returns {Array} List of category objects
     */
    availableCategories: function() {
      return this.categories || []
    },

    /**
     * Available groups based on selected categories (cascading filter).
     * Only shows groups that belong to at least one selected category.
     * @returns {Array} List of group objects
     */
    availableGroups: function() {
      if (this.selectedCategories.length === 0) {
        return []
      }

      var groups = []
      var selectedCatIds = this.selectedCategories.map(function(category) {
        return category.id
      })

      if (!this.allPlans || this.allPlans.length === 0) {
        return []
      }

      this.allPlans.forEach(function(plan) {
        if (plan.category && selectedCatIds.includes(plan.category.id)) {
          groups.push({
            id: plan.id,
            name: plan.name,
            categoryId: plan.category.id
          })
        }
      })

      return groups
    },

    /**
     * Available types based on selected groups (cascading filter).
     * Only shows types that belong to at least one selected group.
     * @returns {Array} List of type objects
     */
    availableTypes: function() {
      if (this.selectedGroups.length === 0) {
        return []
      }

      var types = []
      var selectedGroupIds = this.selectedGroups.map(function(group) {
        return group.id
      })

      if (!this.allActions || this.allActions.length === 0) {
        return []
      }

      this.allActions.forEach(function(action) {
        if (action.plan && selectedGroupIds.includes(action.plan.id)) {
          types.push({
            id: action.id,
            name: action.name,
            groupId: action.plan.id
          })
        }
      })

      return types
    },

    /**
     * The filter variables ready to be sent to the backend.
     * Use this computed property when integrating with your API.
     * @returns {Object} Filter payload
     */
    filterVariables: function() {
      return {
        filterMode: this.filterMode,
        categoryIds: this.selectedCategories.map(function(category) {
          return category.id
        }),
        groupIds: this.selectedGroups.map(function(group) {
          return group.id
        }),
        typeIds: this.selectedTypes.map(function(type) {
          return type.id
        })
      }
    }
  },

  methods: {
    /**
     * Toggles the accordion open/closed state.
     */
    toggleAccordion: function() {
      this.isOpen = !this.isOpen
    },

    // --- Transition hooks ---

    /**
     * Called after the accordion enter transition finishes.
     * Removes overflow hidden so dropdowns can render outside the container.
     * @param {HTMLElement} el - The accordion body element
     */
    onAfterEnter: function(el) {
      el.style.overflow = "visible"
      el.style.maxHeight = "none"
    },

    /**
     * Called before the accordion leave transition starts.
     * Re-applies overflow hidden and a fixed max-height so the
     * closing animation works correctly.
     * @param {HTMLElement} el - The accordion body element
     */
    onBeforeLeave: function(el) {
      el.style.maxHeight = el.scrollHeight + "px"
      el.style.overflow = "hidden"
      // Force a reflow so the browser registers the max-height before transitioning to 0
      void el.offsetHeight
    },

    // --- Toggle selection methods ---

    /**
     * Toggles the selection of a category.
     * When a category is deselected, its associated groups and types
     * are also removed (cascading deselection).
     * @param {Object} option - The category object { id, name }
     */
    toggleCategory: function(option) {
      var index = -1
      for (var i = 0; i < this.selectedCategories.length; i++) {
        if (this.selectedCategories[i].id === option.id) {
          index = i
          break
        }
      }

      if (index !== -1) {
        this.selectedCategories.splice(index, 1)
        this.removeCascadingGroups(option.id)
      } else {
        this.selectedCategories.push({ id: option.id, name: option.name })
      }
    },

    /**
     * Toggles the selection of a group.
     * When a group is deselected, its associated types are also removed.
     * @param {Object} option - The group object { id, name, categoryId }
     */
    toggleGroup: function(option) {
      var index = -1
      for (var i = 0; i < this.selectedGroups.length; i++) {
        if (this.selectedGroups[i].id === option.id) {
          index = i
          break
        }
      }

      if (index !== -1) {
        this.selectedGroups.splice(index, 1)
        this.removeCascadingTypes(option.id)
      } else {
        this.selectedGroups.push({
          id: option.id,
          name: option.name,
          categoryId: option.categoryId
        })
      }
    },

    /**
     * Toggles the selection of a type.
     * @param {Object} option - The type object { id, name, groupId }
     */
    toggleType: function(option) {
      var index = -1
      for (var i = 0; i < this.selectedTypes.length; i++) {
        if (this.selectedTypes[i].id === option.id) {
          index = i
          break
        }
      }

      if (index !== -1) {
        this.selectedTypes.splice(index, 1)
      } else {
        this.selectedTypes.push({
          id: option.id,
          name: option.name,
          groupId: option.groupId
        })
      }
    },

    // --- Cascading removal helpers ---

    /**
     * Removes all selected groups (and their types) that belong to a given category.
     * @param {String} categoryId - The category being deselected
     */
    removeCascadingGroups: function(categoryId) {
      var self = this
      var groupsToRemove = []

      this.selectedGroups.forEach(function(g) {
        if (g.categoryId === categoryId) {
          groupsToRemove.push(g.id)
        }
      })

      groupsToRemove.forEach(function(groupId) {
        self.removeCascadingTypes(groupId)
      })

      this.selectedGroups = this.selectedGroups.filter(function(g) {
        return g.categoryId !== categoryId
      })
    },

    /**
     * Removes all selected types that belong to a given group.
     * @param {String} groupId - The group being deselected
     */
    removeCascadingTypes: function(groupId) {
      this.selectedTypes = this.selectedTypes.filter(function(t) {
        return t.groupId !== groupId
      })
    },

    // --- Action buttons ---

    /**
     * Emits the current filter state to the parent component.
     */
    applyFilters: function() {
      this.$emit("filters-applied", this.filterVariables)
    },

    /**
     * Resets all filter selections and emits the cleared event.
     */
    clearFilters: function() {
      this.selectedCategories = []
      this.selectedGroups = []
      this.selectedTypes = []
      this.filterMode = "include"
      this.$emit("filters-cleared")
    }
  }
}
</script>

<style scoped>
.filter-attentions {
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  margin-bottom: 16px;
  background: #fafafa;
}

.filter-header {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: space-between;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.filter-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.toggle-btn {
  background: none;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-transition: -webkit-transform 0.3s ease;
  transition: transform 0.3s ease;
}

.toggle-icon {
  display: inline-block;
  font-size: 18px;
  -webkit-transform: rotate(-90deg);
  transform: rotate(-90deg);
  -webkit-transition: -webkit-transform 0.3s ease;
  transition: transform 0.3s ease;
}

.toggle-btn.is-open .toggle-icon {
  font-size: 14px;
  -webkit-transform: rotate(0deg);
  transform: rotate(0deg);
}

.accordion-enter-active {
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;
  max-height: 600px;
  overflow: hidden;
  opacity: 1;
}

.accordion-leave-active {
  -webkit-transition: all 0.3s ease;
  transition: all 0.3s ease;
  max-height: 600px;
  overflow: hidden;
  opacity: 1;
}

.accordion-enter,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.filter-body {
  padding: 0 16px 16px 16px;
  border-top: 1px solid #eee;
}

.filter-mode {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  padding: 12px 0 24px 0;
}

.radio-label {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  color: #555;
  margin-right: 24px;
}

.radio-label input[type="radio"] {
  margin-right: 6px;
}

.filter-selectors {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.filter-actions {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  gap: 12px;
  padding-top: 4px;
}

@media screen and (max-width: 998px) {
  .filter-selectors {
    flex-direction: column;
  }

  .filter-actions {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
